// /frontend/src/pages/legal/PrivacyNotice.jsx
// Model Privacy Notice - Plain language summary for Medicare enrollees
// Based on ONC Model Privacy Notice (MPN) template
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Eye, Share2, Trash2, Bell, HelpCircle } from "lucide-react";

export default function PrivacyNotice() {
  const lastUpdated = "December 11, 2025";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#172A3A] via-[#1e3a4c] to-[#172A3A]">
      {/* Header */}
      <header className="bg-[#172A3A]/80 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#FFB800] transition">
            <ArrowLeft size={20} />
            <span className="font-semibold">Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Pro IRP" className="w-8 h-8 rounded-full bg-white" />
            <span className="text-xl font-bold text-white">Pro <span className="text-[#FFB800]">IRP</span></span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#172A3A] rounded-full mb-4">
              <Shield className="w-7 h-7 text-[#FFB800]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#172A3A] mb-2">Privacy Notice</h1>
            <p className="text-gray-500 text-sm">Plain Language Summary</p>
            <p className="text-gray-400 text-sm">Last Updated: {lastUpdated}</p>
          </div>

          {/* CMS Notice */}
          <div className="bg-blue-50 rounded-xl p-4 mb-8 border border-blue-200">
            <p className="text-blue-800 text-sm font-medium">
              This app uses Medicare Blue Button but is not approved or endorsed by Medicare or the U.S. Government.
            </p>
          </div>

          {/* What is this notice */}
          <p className="text-gray-700 mb-8 text-lg leading-relaxed">
            This notice explains, in simple terms, how Pro IRP handles your Medicare health information.
            For complete details, please read our full <Link to="/privacy" className="text-[#FFB800] hover:underline font-semibold">Privacy Policy</Link>.
          </p>

          {/* Summary Cards */}
          <div className="space-y-6">

            {/* What We Collect */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#172A3A] rounded-lg">
                  <Eye className="w-5 h-5 text-[#FFB800]" />
                </div>
                <h2 className="text-xl font-bold text-[#172A3A]">What We Collect</h2>
              </div>
              <ul className="text-gray-700 space-y-2 ml-2">
                <li>Your prescription drug history (Part D)</li>
                <li>Your doctor visits and hospital stays (Parts A & B)</li>
                <li>Your Medicare coverage information</li>
                <li>Basic information like your name and Medicare number</li>
              </ul>
              <p className="text-gray-600 mt-3 text-sm bg-white rounded-lg p-3">
                <strong>When:</strong> We only collect your data when you give permission through Medicare.gov.
                Your insurance agent can refresh your data when needed to help you with your coverage.
              </p>
            </div>

            {/* How We Use It */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#172A3A] rounded-lg">
                  <HelpCircle className="w-5 h-5 text-[#FFB800]" />
                </div>
                <h2 className="text-xl font-bold text-[#172A3A]">How We Use Your Information</h2>
              </div>
              <ul className="text-gray-700 space-y-2 ml-2">
                <li>To help your insurance agent find the best Medicare plan for you</li>
                <li>To check if your medications are covered by your plan</li>
                <li>To remind you about important dates like enrollment periods</li>
                <li>To alert your agent if something changes with your prescriptions</li>
              </ul>
              <p className="text-green-700 mt-3 text-sm bg-green-50 rounded-lg p-3 font-medium">
                We ONLY use your data to help you with your Medicare coverage.
              </p>
            </div>

            {/* Who We Share With */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#172A3A] rounded-lg">
                  <Share2 className="w-5 h-5 text-[#FFB800]" />
                </div>
                <h2 className="text-xl font-bold text-[#172A3A]">Who We Share Your Information With</h2>
              </div>
              <ul className="text-gray-700 space-y-2 ml-2">
                <li><strong>Your insurance agent</strong> - so they can help you with your coverage</li>
                <li><strong>Our technology providers</strong> - who help us run this app securely</li>
              </ul>
              <div className="mt-3 space-y-2">
                <p className="text-red-700 text-sm bg-red-50 rounded-lg p-3 font-medium">
                  We do NOT sell your information to anyone. Ever.
                </p>
                <p className="text-red-700 text-sm bg-red-50 rounded-lg p-3 font-medium">
                  We do NOT share your information with other companies for advertising.
                </p>
              </div>
            </div>

            {/* Your Control */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#172A3A] rounded-lg">
                  <Trash2 className="w-5 h-5 text-[#FFB800]" />
                </div>
                <h2 className="text-xl font-bold text-[#172A3A]">You Are In Control</h2>
              </div>
              <ul className="text-gray-700 space-y-3 ml-2">
                <li>
                  <strong>Stop access anytime:</strong> Tell your agent or contact us, and we will stop
                  accessing your Medicare data immediately.
                </li>
                <li>
                  <strong>Delete your data:</strong> When you revoke access, we delete your Medicare
                  information within 30 days.
                </li>
                <li>
                  <strong>See your data:</strong> You can ask to see what information we have about you
                  at any time.
                </li>
                <li>
                  <strong>Fix mistakes:</strong> If any of your information is wrong, let us know and
                  we will correct it.
                </li>
              </ul>
            </div>

            {/* If Something Goes Wrong */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#172A3A] rounded-lg">
                  <Bell className="w-5 h-5 text-[#FFB800]" />
                </div>
                <h2 className="text-xl font-bold text-[#172A3A]">If Something Goes Wrong</h2>
              </div>
              <p className="text-gray-700 mb-3">
                If your information is ever accessed by someone who shouldn't have it (a "data breach"),
                we will:
              </p>
              <ul className="text-gray-700 space-y-2 ml-2">
                <li>Tell you within 60 days</li>
                <li>Explain what happened and what information was affected</li>
                <li>Tell you what steps you can take to protect yourself</li>
                <li>Report it to the government as required by law</li>
              </ul>
            </div>

          </div>

          {/* Contact */}
          <div className="mt-8 bg-[#172A3A] rounded-xl p-6 text-white">
            <h2 className="text-xl font-bold mb-3">Questions?</h2>
            <p className="text-white/80 mb-4">
              If you have questions about your privacy or want to exercise your rights, contact us:
            </p>
            <p className="text-[#FFB800] font-semibold">fluegel.scott@proirp.com</p>
          </div>

          {/* Links */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-gray-500 text-sm mb-4">
              This is a summary. For complete details, please read:
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/privacy" className="text-[#FFB800] hover:underline font-semibold">Full Privacy Policy</Link>
              <span className="text-gray-300">|</span>
              <Link to="/terms" className="text-[#FFB800] hover:underline font-semibold">Terms of Service</Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-white/60 text-sm">
        <p>&copy; {new Date().getFullYear()} Pro IRP. All rights reserved.</p>
      </footer>
    </div>
  );
}
